import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, getUser, clearAuth } from "@/lib/api";
import { Search, Menu, X, Newspaper, ChevronDown, UserPlus } from "lucide-react";

// Any parent nav item whose label/path looks like "Directory" gets an extra
// highlighted "Add My Profile" pill pinned to the top of its dropdown.
const isDirectoryItem = (item) => {
  const l = (item?.label || "").toLowerCase();
  const p = (item?.path  || "").toLowerCase();
  return l.includes("director") || p.includes("director") || p.startsWith("/directory");
};

function NavItem({ item, children }) {
  const [open, setOpen] = useState(false);
  const isExternal = /^https?:\/\//.test(item.path);
  const testId = `nav-${item.path.replace(/[/:]/g, "-")}`;
  const baseCls = "whitespace-nowrap px-3.5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1";
  const showDirectoryCTA = isDirectoryItem(item);

  const label = (
    <span className="flex items-center gap-1">{item.label}
      {(children.length > 0 || showDirectoryCTA) && <ChevronDown className="w-3.5 h-3.5 opacity-60"/>}
    </span>
  );

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-testid={`nav-parent-${item.id}`}>
      {isExternal ? (
        <a href={item.path} target={item.target || "_self"} rel="noreferrer" className={`${baseCls} border-transparent text-slate-600 hover:text-slate-900`} data-testid={testId}>{label}</a>
      ) : (
        <NavLink to={item.path} end={item.path === "/"} target={item.target || "_self"}
          className={({ isActive }) => `${baseCls} ${isActive ? "border-sky-700 text-sky-800" : "border-transparent text-slate-600 hover:text-slate-900"}`}
          data-testid={testId}>{label}</NavLink>
      )}
      {(children.length > 0 || showDirectoryCTA) && open && (
        <div className="absolute left-0 top-full mt-0.5 bg-white rounded-2xl shadow-lg border border-slate-100 py-2 min-w-[240px] z-50" data-testid={`nav-submenu-${item.id}`}>
          {showDirectoryCTA && (
            <div className="px-2 pb-2 mb-1 border-b border-slate-100">
              <Link
                to="/add-my-profile"
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-bold shadow-sm hover:-translate-y-0.5 transition-transform"
                style={{
                  background: "linear-gradient(135deg,#F59E0B 0%,#F0C948 100%)",
                  color: "#0F172A",
                  border: "1px solid #E7DCB9",
                }}
                data-testid="nav-add-my-profile"
              >
                <UserPlus className="w-4 h-4" />
                Add My Profile
              </Link>
            </div>
          )}
          {children.sort((a, b) => a.order - b.order).map(c => {
            const isExt = /^https?:\/\//.test(c.path);
            const cTest = `submenu-${c.path.replace(/[/:]/g, "-")}`;
            const cls = "block px-4 py-2 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-800";
            return isExt
              ? <a key={c.id} href={c.path} target={c.target || "_self"} rel="noreferrer" className={cls} data-testid={cTest}>{c.label}</a>
              : <Link key={c.id} to={c.path} target={c.target || "_self"} className={cls} data-testid={cTest}>{c.label}</Link>;
          })}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [suggest, setSuggest] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [items, setItems] = useState([]);
  const user = getUser();

  useEffect(() => {
    api.get("/menu").then(r => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setSuggest(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/suggest`, { params: { q } });
        setSuggest(data);
      } catch { setSuggest(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
    setSuggest(null);
  };

  const logout = () => { clearAuth(); nav("/"); window.location.reload(); };

  return (
    <header className="sticky top-0 z-40 glass-nav" data-testid="site-header">
      {/* Masthead */}
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="site-logo">
          <div className="w-10 h-10 rounded-2xl bg-sky-100 grid place-items-center">
            <Newspaper className="w-5 h-5 text-sky-700" />
          </div>
          <div>
            <div className="font-editorial text-2xl font-black leading-none" style={{ color: "#D22B2B" }}>EduTime<span> Today</span></div>
            <div className="overline text-slate-500 text-[9px] mt-0.5">India's AI Powered Education news Platform</div>
          </div>
        </Link>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-lg relative">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search news, colleges, rankings, scholarships…"
              className="w-full pl-11 pr-4 py-2.5 rounded-full bg-slate-100 border border-slate-100 focus:bg-white focus:border-sky-300 outline-none text-sm"
              data-testid="header-search-input"
            />
          </div>
          {suggest && (suggest.articles?.length > 0 || suggest.institutions?.length > 0) && (
            <div className="absolute top-12 left-0 right-0 bg-white rounded-2xl shadow-lg border border-slate-100 p-2 z-50" data-testid="search-suggest-panel">
              {suggest.articles?.slice(0, 5).map(a => (
                <button key={a.slug} type="button" onClick={() => { nav(`/article/${a.slug}`); setSuggest(null); setQ(""); }} className="w-full text-left px-3 py-2 rounded-xl hover:bg-sky-50 text-sm text-slate-700">
                  <span className="text-[10px] overline text-sky-700 mr-2">News</span>{a.title}
                </button>
              ))}
              {suggest.institutions?.slice(0, 3).map(i => (
                <button key={i.slug} type="button" onClick={() => { nav(`/institution/${i.slug}`); setSuggest(null); setQ(""); }} className="w-full text-left px-3 py-2 rounded-xl hover:bg-emerald-50 text-sm text-slate-700">
                  <span className="text-[10px] overline text-emerald-700 mr-2">{i.type}</span>{i.name}
                </button>
              ))}
            </div>
          )}
        </form>

        <button onClick={() => setOpenMenu(!openMenu)} className="md:hidden p-2 rounded-xl border border-slate-200" data-testid="mobile-menu-toggle">
          {openMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Category nav */}
      <nav className="border-t border-slate-100 bg-white/70">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap gap-1 items-center relative" data-testid="primary-nav">
          {items.filter(i => !i.parent_id).map((n) => {
            const children = items.filter(c => c.parent_id === n.id);
            return <NavItem key={n.id} item={n} children={children} />;
          })}
        </div>
      </nav>

      {openMenu && (
        <div className="md:hidden border-t border-slate-100 bg-white p-3" data-testid="mobile-menu">
          <form onSubmit={onSearch} className="mb-2">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..." className="w-full px-4 py-2 rounded-full bg-slate-100 text-sm" />
          </form>
          {items.filter(i => !i.parent_id).map(n => {
            const children = items.filter(c => c.parent_id === n.id);
            const showCTA = isDirectoryItem(n);
            return (
              <div key={n.id}>
                <Link to={n.path} onClick={() => setOpenMenu(false)} className="block px-3 py-2 text-sm text-slate-700 hover:bg-sky-50 rounded-lg">{n.label}</Link>
                {showCTA && (
                  <Link to="/add-my-profile" onClick={() => setOpenMenu(false)}
                    className="flex items-center gap-2 mx-3 my-1 px-3 py-2 rounded-full text-sm font-bold"
                    style={{ background: "linear-gradient(135deg,#F59E0B 0%,#F0C948 100%)", color: "#0F172A" }}
                    data-testid="mobile-add-my-profile">
                    <UserPlus className="w-4 h-4"/> Add My Profile
                  </Link>
                )}
                {children.map(c => (
                  <Link key={c.id} to={c.path} onClick={() => setOpenMenu(false)} className="block pl-8 pr-3 py-2 text-sm text-slate-600 hover:bg-sky-50 rounded-lg">— {c.label}</Link>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </header>
  );
}
